import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Badge,
  Button,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications,
  DarkMode,
  LightMode,
  Person,
  Settings,
  Logout,
  SportsCricket,
  SportsEsports,
  Dashboard,
  Group,
  Business,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  MarkAsUnread,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import PWAInstallButton from '../PWA/PWAInstallButton';

const Header = ({ onMenuClick, onThemeToggle, isDarkMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, getTimeAgo } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Menu anchors
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);

  // Quick navigation links - Essential and working routes
  const quickLinks = [
    { 
      title: 'Dashboard', 
      path: '/dashboard', 
      icon: Dashboard,
      show: isAuthenticated,
    },
    { 
      title: 'Games', 
      path: '/games', 
      icon: SportsEsports,
      show: isAuthenticated,
    },
    { 
      title: 'Teams', 
      path: '/teams', 
      icon: Group,
      show: isAuthenticated,
    },
    { 
      title: 'Players', 
      path: '/players', 
      icon: Person,
      show: isAuthenticated,
    },
  ];

  // Event handlers
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenu = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
    handleClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: isDarkMode 
          ? 'rgba(15, 23, 42, 0.95)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: isDarkMode 
          ? '1px solid rgba(148, 163, 184, 0.1)' 
          : '1px solid rgba(226, 232, 240, 0.8)',
        color: isDarkMode ? '#ffffff' : '#1e293b',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Toolbar 
        sx={{ 
          minHeight: { xs: 56, md: 64 },
          px: { xs: 1, sm: 2, md: 3 },
          maxWidth: '100vw',
          overflow: 'hidden',
        }}
        disableGutters
      >

        {/* Mobile Menu Button */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMenuClick}
          sx={{ 
            mr: 1,
            display: { lg: 'flex', xl: 'none' },
            p: 1,
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          <MenuIcon sx={{ fontSize: 24 }} />
        </IconButton>
        
        {/* Logo & Brand - Simplified */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.8,
            }
          }}
          onClick={() => navigate('/')}
        >
          <SportsCricket 
            sx={{ 
              fontSize: 32, 
              mr: 1, 
              color: '#3b82f6',
            }} 
          />
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.2rem', md: '1.4rem' },
              color: isDarkMode ? '#ffffff' : '#1e293b',
            }}
          >
            SeriesX
          </Typography>
        </Box>

        {/* Desktop Navigation Links - Simplified */}
        {isAuthenticated && (
          <Box sx={{ 
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center', 
            ml: 4, 
            gap: 2,
            flexGrow: 1,
            maxWidth: '600px', // Prevent overflow
          }}>
            {/* Quick Action Links */}
            {quickLinks.filter(link => link.show).map((link) => (
              <Button
                key={link.path}
                onClick={() => navigate(link.path)}
                startIcon={<link.icon sx={{ fontSize: 20 }} />}
                sx={{
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  textTransform: 'none',
                  fontWeight: isActiveRoute(link.path) ? 600 : 500,
                  fontSize: '0.9rem',
                  px: 2,
                  py: 1,
                  borderRadius: 1.5,
                  backgroundColor: isActiveRoute(link.path) 
                    ? (isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)')
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(51, 65, 85, 0.5)' : 'rgba(248, 250, 252, 0.8)',
                    color: isDarkMode ? '#ffffff' : '#1e293b',
                  },
                }}
              >
                {link.title}
              </Button>
            ))}
          </Box>
        )}

        {/* Right Side Actions - Simplified */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          ml: 'auto'
        }}>
          {/* PWA Install Button */}
          <PWAInstallButton 
            variant="icon" 
            color={isDarkMode ? '#cbd5e1' : '#64748b'}
          />
          
          {/* Theme Toggle */}
          <IconButton 
            onClick={onThemeToggle}
            sx={{
              p: 1.5,
              borderRadius: 2,
              color: isDarkMode ? '#fbbf24' : '#f59e0b',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(251, 191, 36, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                transform: 'rotate(15deg)',
              },
            }}
          >
            {isDarkMode ? <LightMode sx={{ fontSize: 20 }} /> : <DarkMode sx={{ fontSize: 20 }} />}
          </IconButton>

          {isAuthenticated ? (
            <>
              {/* Notifications */}
              <IconButton 
                onClick={handleNotificationMenu}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  color: isDarkMode ? '#cbd5e1' : '#64748b',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(203, 213, 225, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                  },
                }}
              >
                <Badge 
                  badgeContent={unreadCount} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.7rem',
                      height: 18,
                      minWidth: 18,
                    }
                  }}
                >
                  <Notifications sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>

              {/* User Profile Info - Only on larger screens */}
              {!isMobile && (
                <Box sx={{ 
                  textAlign: 'right', 
                  mr: 1,
                  display: { xs: 'none', md: 'block' }
                }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={600} 
                    sx={{ 
                      lineHeight: 1.2,
                      color: isDarkMode ? '#e2e8f0' : '#1e293b',
                    }}
                  >
                    {user?.name || user?.username}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      lineHeight: 1,
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                    }}
                  >
                    {user?.role === 'admin' ? 'Admin' : 'Player'}
                  </Typography>
                </Box>
              )}
              
              {/* User Avatar & Menu */}
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                sx={{
                  p: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 36, 
                    height: 36,
                    bgcolor: '#3b82f6',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  {user?.name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/auth/login')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  fontSize: '0.9rem',
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  borderColor: isDarkMode ? 'rgba(226, 232, 240, 0.3)' : 'rgba(71, 85, 105, 0.3)',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(226, 232, 240, 0.1)' : 'rgba(71, 85, 105, 0.1)',
                  },
                }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/auth/signup')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  fontSize: '0.9rem',
                  bgcolor: '#3b82f6',
                  '&:hover': {
                    bgcolor: '#2563eb',
                  },
                }}
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Box>

        {/* Notification Menu */}
        <Menu
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationClose}
          PaperProps={{
            sx: { 
              width: 360, 
              maxHeight: 500,
              borderRadius: 2,
              backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode ? '1px solid rgba(71, 85, 105, 0.3)' : '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: isDarkMode 
                ? '0 20px 50px rgba(0, 0, 0, 0.3)' 
                : '0 20px 50px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: isDarkMode ? '1px solid rgba(71, 85, 105, 0.3)' : '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Notifications ({unreadCount})
            </Typography>
            {notifications.length > 0 && (
              <Button
                size="small"
                onClick={() => {
                  markAllAsRead();
                  handleNotificationClose();
                }}
                startIcon={<DoneAllIcon />}
                sx={{ fontSize: '0.75rem' }}
              >
                Mark All Read
              </Button>
            )}
          </Box>
          {notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            notifications.map((notification) => (
              <MenuItem 
                key={notification._id}
                onClick={() => {
                  if (!notification.isRead) {
                    markAsRead(notification._id);
                  }
                  // Handle notification click based on type
                  if (notification.type === 'game_invitation' && notification.data?.gameCode) {
                    navigate(`/join-game?search=${notification.data.gameCode}`);
                  }
                  handleNotificationClose();
                }}
                sx={{ 
                  py: 2, 
                  px: 2,
                  backgroundColor: notification.isRead 
                    ? 'transparent' 
                    : isDarkMode 
                      ? 'rgba(59, 130, 246, 0.1)' 
                      : 'rgba(59, 130, 246, 0.05)',
                  '&:hover': {
                    backgroundColor: isDarkMode 
                      ? 'rgba(71, 85, 105, 0.2)' 
                      : 'rgba(226, 232, 240, 0.3)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <Box sx={{ flex: 1, pr: 1 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={notification.isRead ? 400 : 600}
                      sx={{ mb: 0.5 }}
                    >
                      {notification.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 0.5, fontSize: '0.85rem' }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getTimeAgo(notification.createdAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {!notification.isRead && (
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification._id);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <MarkAsUnread sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification._id);
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              </MenuItem>
            ))
          )}
        </Menu>

        {/* User Menu */}
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 2,
              minWidth: 200,
              backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode ? '1px solid rgba(71, 85, 105, 0.3)' : '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: isDarkMode 
                ? '0 20px 50px rgba(0, 0, 0, 0.3)' 
                : '0 20px 50px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <MenuItem onClick={handleProfile} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon>
              <Person fontSize="small" />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleSettings} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, px: 2, color: 'error.main' }}>
            <ListItemIcon>
              <Logout fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;